import { v4 as uuidv4 } from "uuid"

import type { Variant } from "../features/ImportFlow/Interfaces"
import type { GroupConditions, RuleGroupConditions } from "../features/Ruleset"
import type { TagGroupConditions } from "../features/Taglist"
import { Operators } from "../types/custom/projects"
import { type CustomRuleRequest, type CustomTagRequest, type Rule } from "../types/custom/rules"
import type { RulesetRuleList } from "../types/generated/api/RulesetRuleList"
import type { TagListRuleList } from "../types/generated/api/TagListRuleList"
import { WorkflowInputFeature } from "../types/generated/api/WorkflowInputFeature"
import { ConditionParser, parseConditionIntoString } from "./conditionHelpers"

// Constants
const FILE_UUID_PATTERN = /\$file<[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}>/
const FILE_ID_MATCHER = /\$file<([^>]+)>/
const FILE_UUID_CLEANUP_PATTERN =
  /(?<=\$file<\b[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12})@\d+\.\d+(?=>)/g

// cache sorted operators to avoid re-calculating them on every call
const SORTED_OPERATORS = Object.keys(Operators)
  .filter((key) => isNaN(Number(key)))
  .sort((a, b) => b.length - a.length)

// Type for the parsed conditions array structure
type ParsedConditionsArray = Array<[string, ...string[]]>

/**
 * all related helpers for ruleset component.
 */

export const reorderList = (list: RuleGroupConditions[], name: string, direction: string): RuleGroupConditions[] => {
  // Create a new list
  const newList = [...list]
  // Find the index of the object with the specified id
  const index = newList.findIndex((obj) => obj.name === name)
  // Check if the direction is "up"
  if (direction === "up" && index > 0) {
    // Remove the object at the specified index and insert it at the previous index
    newList.splice(index - 1, 0, newList.splice(index, 1)[0] as RuleGroupConditions)
  }

  // Check if the direction is "down"
  else if (direction === "down" && index < newList.length - 1) {
    // Remove the object at the specified index and insert it at the next index
    newList.splice(index + 1, 0, newList.splice(index, 1)[0] as RuleGroupConditions)
  }

  return newList
}

// Get all indices for a specific string, ignoring matches within string literals
const indexAll = (str: string, search: string): number[] => {
  const indices: number[] = []
  const searchLength = search.length
  let inString = false
  let stringChar = ""

  for (let i = 0; i <= str?.length - searchLength; i++) {
    // Track if we're inside a string literal
    if (str[i] === "'" && (i === 0 || str[i - 1] !== "\\")) {
      if (!inString) {
        inString = true
        stringChar = str[i] as string
      } else if (str[i] === stringChar) {
        inString = false
      }
    }

    // Only check for matches if we're not inside a string
    if (!inString && str.slice(i, i + searchLength) === search) {
      indices.push(i)
    }
  }

  return indices
}

export const extractConditionsFromParentheses = (str: string): string[] => {
  const result = []
  let current = ""
  let depth = 0
  let inString = false
  let stringChar = ""

  for (const char of str) {
    // Handle string literals
    if ((char === "'" || char === '"') && !inString) {
      inString = true
      stringChar = char
    } else if (char === stringChar && inString) {
      inString = false
    }

    // Only process parentheses if we're not inside a string
    if (!inString) {
      if (char === "(") {
        depth++
        // Only skip the outermost opening parenthesis
        if (depth === 1) continue
      }
      if (char === ")") {
        depth--
        // Only handle the outermost closing parenthesis
        if (depth === 0) {
          result.push(current.trim())
          current = ""
          continue
        }
      }
    }

    // Add character if we're inside at least one set of parentheses
    if (depth > 0) {
      current += char
    }
  }
  return result
}

export const convertComplexConditionsToNestedForm = (
  condition: string,
): { rules: Rule[][]; switches: { group1?: string; group2?: string } } => {
  if (!condition || typeof condition !== "string") {
    return { rules: [], switches: { group1: "and", group2: "and" } }
  }

  // Arrays to store the intermediate parsing results
  const parsedLevels: string[] = []
  const parsedConditions: ParsedConditionsArray = []
  const result: Rule[][] = []

  // Find all occurrences of "((" and "))" to identify condition group boundaries
  const startIndices = indexAll(condition, "((")
  const endIndices = indexAll(condition, "))")

  condition = condition.replace(FILE_UUID_CLEANUP_PATTERN, "")

  // Array to store the logical operators (and/or) between condition groups
  const switches = []

  // Extract each condition group from the string
  for (let i = 0; i < startIndices.length; i++) {
    // Calculate the start and end positions for substring extraction
    // Special handling for first group (start - 1) and last group (endIndices[0] + 1)
    const start = i === 0 ? (startIndices[i] as number) - 1 : (startIndices[i] as number) + 1
    const end = i === startIndices.length - 1 ? (endIndices[0] as number) + 1 : startIndices[i + 1]
    const subCondition = condition.substring(i === 0 ? start + 2 : start, end)
    parsedLevels.push(subCondition)

    // Extract the logical operator (and/or) between groups
    if (i > 0) {
      switches.push(condition.slice((startIndices[i] as number) - 4, startIndices[i]).trim())
    }
  }

  // Process each condition group to determine its internal logical operator
  parsedLevels.forEach((item) => {
    // If the group contains ") or (", its internal operator is "or", otherwise "and"
    const operator = item.includes(") or (") ? "or" : "and"
    const conditions = extractConditionsFromParentheses(item)
    parsedConditions.push([operator, ...conditions])
  })

  // Convert parsed conditions into Rule objects
  for (const element of parsedConditions) {
    const tempArr: Rule[] = []
    const operator = element[0] // First element is the logical operator

    // Skip first element as it's the logical operator
    for (let j = 1; j < element.length; j++) {
      const conditionString = element[j]
      if (!conditionString) continue

      // Parse each condition string into its components using ConditionParser
      const parseCondition = new ConditionParser(conditionString)
      tempArr.push({
        id: uuidv4(),
        feature: parseCondition.getFeature(),
        andOr: operator, // Use the group's logical operator
        operator: parseCondition.getOperator(),
        type: parseCondition.getType(),
        valueOrFeature: parseCondition.getRHSIndicator(),
        value: parseCondition.getValue(),
        secondValue: parseCondition.getSecondValue(),
        secondFeature: parseCondition.getSecondFeature(),
        schemaFeatureType: WorkflowInputFeature.type.TEXT,
      })
    }
    result.push(tempArr)
  }

  // Return the parsed rules and the logical operators between groups
  // Default to "and" if no operator is found
  return { rules: result, switches: { group1: switches?.[0] ?? "and", group2: switches?.[1] ?? "and" } }
}

export function compareTwoRulesList(
  arr1: CustomRuleRequest[] | CustomTagRequest[],
  arr2: RulesetRuleList[] | CustomTagRequest[] | TagListRuleList[],
): boolean {
  if (arr1.length !== arr2.length) return false
  for (let i = 0; i < arr1.length; i++) {
    const firstArray = arr1[i] as RulesetRuleList
    const secondArray = arr2[i] as RulesetRuleList

    if (
      firstArray?.return_label !== secondArray?.return_label ||
      firstArray.name !== secondArray.name ||
      firstArray.condition !== secondArray.condition
    ) {
      return false
    }
  }
  return true
}

export function isTypeIncluded(type: string): boolean {
  return ["true", "false", "null"].includes(type)
}

export const fileUuidRegex = FILE_UUID_PATTERN

export { FILE_ID_MATCHER }

export function matchFile(value: string): { isMatching: boolean; fileId: string | undefined } {
  if (!value || typeof value !== "string") {
    return { isMatching: false, fileId: undefined }
  }

  const isMatching = Boolean(fileUuidRegex.exec(value))
  return { isMatching, fileId: isMatching ? value.slice(6, -1) : undefined }
}

function convertRulesToConditionString(rules: Rule[]): string | null {
  if (!rules || rules.length === 0) {
    return null
  }

  const conditionParts: string[] = []
  for (const rule of rules) {
    const condition = parseConditionIntoString(rule)
    if (condition) {
      conditionParts.push(condition)
    }
  }

  if (conditionParts.length === 0) {
    return null
  } else if (conditionParts.length === 1) {
    return conditionParts[0] as string
  } else {
    let result = conditionParts[0] ?? ""
    for (let i = 1; i < conditionParts.length; i++) {
      const operator = rules[i]?.andOr ?? "and"
      result += ` ${operator} ${conditionParts[i] ?? ""}`
    }
    return result
  }
}

function convertGroupConditionsToConditionString(group: GroupConditions): string | null {
  const parts: Array<{ content: string; andOr?: string }> = []

  // Process individual conditions in this group - they are siblings to nested groups
  if (group.conditions && group.conditions.length > 0) {
    for (const condition of group.conditions) {
      const conditionString = parseConditionIntoString(condition) ?? ""
      if (conditionString) {
        parts.push({
          content: conditionString,
          andOr: condition.andOr,
        })
      }
    }
  }

  // Process nested groups - they are siblings to individual conditions
  if (group.groups && group.groups.length > 0) {
    for (const nestedGroup of group.groups) {
      const nestedGroupString = convertGroupConditionsToConditionString(nestedGroup)
      if (nestedGroupString) {
        parts.push({
          content: nestedGroupString,
          andOr: nestedGroup.andOr, // Use the nested group's andOr for joining
        })
      }
    }
  }

  if (parts.length === 0) {
    return null
  } else if (parts.length === 1) {
    return parts[0]?.content ?? null
  } else {
    // Join parts using each element's andOr operator (except the first one)
    let result = parts[0]?.content
    for (let i = 1; i < parts.length; i++) {
      // Use the andOr from the current part (which represents how it should join with the previous)
      const operator = parts[i]?.andOr ?? "and"
      result += ` ${operator} ${parts[i]?.content ?? ""}`
    }
    return `(${result})`
  }
}

/**
 * Helper function for parsing individual conditions
 * Converts a condition string into a Rule object with proper feature, operator, and value parsing
 *
 * @param conditionStr - The condition string to parse (e.g., "age > 18", "name = 'John'", "age between [5,25]")
 * @returns A Rule object with parsed feature, operator, value, secondValue, and type information
 */
function parseCondition(conditionStr: string): Rule {
  const trimmed = conditionStr.trim()

  // Remove outer parentheses if they exist for a single condition
  const condition = trimmed.startsWith("(") && trimmed.endsWith(")") ? trimmed.slice(1, -1).trim() : trimmed

  let feature = ""
  let operator = ""
  let value = ""
  let valueOrFeature: "Value" | "Feature" = "Value"
  let secondValue = ""
  let secondFeature = ""
  let type: "string" | "number" | "null" = "string"

  // Find the operator in the condition
  for (const op of SORTED_OPERATORS) {
    // we are using a regex to find the operator in the condition and split the condition into parts
    const regex = new RegExp(`\\s+${op.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s+`, "i")
    const match = condition.match(regex)

    if (match) {
      const parts = condition.split(regex)
      if (parts.length >= 2) {
        feature = parts[0]?.trim() ?? ""
        operator = Operators[op as keyof typeof Operators] ?? ""
        value = parts[1]?.trim() ?? ""

        if (operator === Operators["between"]) {
          // Extract values from between operator format: [value1,value2]
          const matchValues = value.match(/^\[\s*["']?([^"'\],]+)["']?\s*,\s*["']?([^"'\],]+)["']?\s*\]$/)
          if (matchValues) {
            value = matchValues[1] ?? ""
            secondValue = matchValues[2] ?? ""
          }
        }

        // Handle arrays for contains/not contains operators
        if (operator === Operators["contains"] || operator === Operators["not contains"]) {
          // console.log(value)
          value = value.replace(/\[\s*(['"].*?['"](?:\s*,\s*['"].*?['"])*)\s*\]/g, "$1")
        }

        // Strip quotes from single value
        if (
          (operator !== Operators["contains"] &&
            operator !== Operators["not contains"] &&
            value.startsWith("'") &&
            value.endsWith("'")) ||
          (value.startsWith('"') && value.endsWith('"'))
        ) {
          value = value.slice(1, -1)
          secondFeature = value
          valueOrFeature = "Feature"
        }

        if (value === "null") {
          type = "null"
        } else if (!isNaN(Number(value)) && value !== "") {
          type = "number"
        } else {
          type = "string"
        }

        break
      }
    }
  }

  // Handle special cases for null operators
  if (condition.includes(" null")) {
    if (condition.includes(" != null")) {
      operator = Operators["not null"] as string
    } else {
      operator = Operators["null"] as string
    }

    feature = condition.split(" ")[0]?.trim() ?? ""
    value = ""
    type = "null"
  }

  return {
    id: uuidv4(),
    feature,
    andOr: "and",
    operator,
    type,
    valueOrFeature,
    value,
    secondValue,
    secondFeature,
    schemaFeatureType: WorkflowInputFeature.type.TEXT,
  }
}

// Helper function to check if a string is a simple condition (no nested groups)
function isSimpleCondition(str: string): boolean {
  const trimmed = str.trim()

  // Remove outer parentheses if they exist
  let content = trimmed
  if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    content = trimmed.slice(1, -1).trim()
  }

  // Check if it has any nested parentheses
  let inQuotes = false
  let quoteChar = ""

  for (let i = 0; i < content.length; i++) {
    const char = content[i]

    if ((char === '"' || char === "'") && !inQuotes) {
      inQuotes = true
      quoteChar = char
    } else if (char === quoteChar && inQuotes) {
      inQuotes = false
      quoteChar = ""
    } else if (!inQuotes) {
      if (char === "(") {
        return false // Found nested parentheses, not a simple condition
      }
    }
  }

  return true
}

// Parse elements at the top level (after removing outermost parentheses)
function parseTopLevel(content: string): { conditions: Rule[]; groups: GroupConditions[] } {
  const result = { conditions: [] as Rule[], groups: [] as GroupConditions[] }

  const elements: string[] = []
  const operators: string[] = []

  let current = ""
  let depth = 0
  let inQuotes = false
  let quoteChar = ""
  let i = 0

  while (i < content.length) {
    const char = content[i]

    if ((char === '"' || char === "'") && !inQuotes) {
      inQuotes = true
      quoteChar = char
      current += char
    } else if (char === quoteChar && inQuotes) {
      inQuotes = false
      quoteChar = ""
      current += char
    } else if (inQuotes) {
      current += char
    } else if (char === "(") {
      depth++
      current += char
    } else if (char === ")") {
      depth--
      current += char
    } else if (depth === 0) {
      // Check for logical operators at top level
      const remaining = content.substring(i)
      const andMatch = remaining.match(/^\s+and\s+/i)
      const orMatch = remaining.match(/^\s+or\s+/i)

      if (andMatch || orMatch) {
        const operator = andMatch ? "and" : "or"
        const matchLength = andMatch ? andMatch[0].length : (orMatch?.[0]?.length ?? 0)

        elements.push(current.trim())
        operators.push(operator)
        current = ""
        i += matchLength
        continue
      } else {
        current += char
      }
    } else {
      current += char
    }

    i++
  }

  if (current.trim()) {
    elements.push(current.trim())
  }

  // Process each element
  for (let j = 0; j < elements.length; j++) {
    const element = elements[j]
    if (!element) continue

    const andOr = j === 0 ? "and" : (operators[j - 1] ?? "and")

    if (isSimpleCondition(element)) {
      // It's a simple condition - add to conditions array
      const rule = parseCondition(element)
      rule.andOr = andOr
      result.conditions.push(rule)
    } else {
      // It's a complex group - parse recursively
      // eslint-disable-next-line @typescript-eslint/no-use-before-define
      const group = parseGroup(element, andOr as "and" | "or")
      result.groups.push(group)
    }
  }

  return result
}

// Parse a group (something with nested parentheses)
function parseGroup(groupStr: string, andOr: "and" | "or"): GroupConditions {
  const trimmed = groupStr.trim()

  // Remove outer parentheses
  let content = trimmed
  if (trimmed.startsWith("(") && trimmed.endsWith(")")) {
    content = trimmed.slice(1, -1).trim()
  }

  const parsed = parseTopLevel(content)

  return {
    id: uuidv4(),
    andOr,
    conditions: parsed.conditions.length > 0 ? parsed.conditions : undefined,
    groups: parsed.groups,
  }
}

export const convertRuleGroupConditionsToComplexCondition = (
  ruleGroup: RuleGroupConditions | TagGroupConditions,
): string => {
  const parts: string[] = []
  const groupOperators: string[] = []
  // Process level one conditions
  if (ruleGroup.conditions && ruleGroup.conditions.length > 0) {
    const levelOneCondition = convertRulesToConditionString(ruleGroup.conditions)
    if (levelOneCondition) {
      parts.push(levelOneCondition)
    }
  }

  // Process groups
  if (ruleGroup.groups && ruleGroup.groups.length > 0) {
    for (const group of ruleGroup.groups) {
      const groupCondition = convertGroupConditionsToConditionString(group)
      groupOperators.push(group.andOr)
      if (groupCondition) {
        parts.push(groupCondition)
      }
    }
  }

  // Combine all parts with the appropriate operator
  if (parts.length === 0) {
    return ""
  } else if (parts.length === 1) {
    return `(${parts[0] as string})`
  } else {
    // Use the collected operators to join parts
    const joinedParts = parts
      .map((part, index) => {
        if (index === 0) {
          return part
        } else {
          const operator = groupOperators[index - 1] ?? "and"
          return `${operator} ${part}`
        }
      })
      .join(" ")
    return `(${joinedParts})`
  }
}

/**
 * Parses a single condition string into a Rule object.
 * This function uses ConditionParser to extract all components of a condition
 * and creates a properly structured Rule object.
 *
 * @param conditionString - A complex condition string with nested parentheses
 * @param componentType - Either "Ruleset" or "Taglist" to determine return type
 * @returns A RuleGroupConditions or TagGroupConditions object with proper nesting structure
 *
 * @example
 * convertComplexConditionToComponentsGroupConditions("((age > 18) and ((name != 'admin') or (status = 'active')))", "Taglist")
 * // Returns: { conditions: [age > 18], groups: [{ conditions: [name != 'admin', status = 'active'] }] }
 */
export const convertComplexConditionToComponentsGroupConditions = (
  conditionString: string,
  componentType: Variant,
): RuleGroupConditions | TagGroupConditions => {
  if (!conditionString || typeof conditionString !== "string") {
    const baseStructure = {
      id: uuidv4(),
      name: "",
      condition_list_files: [],
    }

    if (componentType === "Ruleset") {
      return {
        ...baseStructure,
        label: "",
        label_id: "",
      } as RuleGroupConditions
    } else {
      return baseStructure as TagGroupConditions
    }
  }

  // Clean up file UUID patterns
  const cleanedCondition = conditionString.replace(FILE_UUID_CLEANUP_PATTERN, "")

  // Remove the outermost wrapping parentheses
  let content = cleanedCondition.trim()
  if (content.startsWith("(") && content.endsWith(")")) {
    content = content.slice(1, -1).trim()
  }

  // Parse the content
  const parsed = parseTopLevel(content)

  // Create the appropriate return structure based on component type
  const baseStructure = {
    id: uuidv4(),
    name: "",
    conditions: parsed.conditions.length > 0 ? parsed.conditions : undefined,
    groups: parsed.groups.length > 0 ? parsed.groups : undefined,
    condition_list_files: [],
  }

  if (componentType === "Ruleset") {
    return {
      ...baseStructure,
      label: "",
      label_id: "",
    } as RuleGroupConditions
  } else {
    return baseStructure as TagGroupConditions
  }
}
